import { Ray, type Scene, UniversalCamera, Vector3 } from "@babylonjs/core";
import { EYE } from "./body";
import { CollisionWorld } from "./collisionWorld";
import { SPAWN_LIFT, spawnFeetY } from "./physics";
import { walkStep } from "./walker";
import type { BuiltWorld } from "./worldBuild";
import { $cameraSettings, cameraParams } from "../state/settings";
import { applyCamera } from "./cameraSettings";
import { applyLook, applyTurn } from "../input/look";
import { bindKeyboard, input as sharedInput, type InputState } from "../input/state";
import { $touchMode } from "../state/touch";

const WALK = 4.5, SPRINT = 7.5;

/** What the walker stands on: the lobby's terrain or the room's floor (spec 2o §4.2). */
export interface WalkSpace {
  height(x: number, z: number): number;
  /** Called every frame at the player's position (the lobby streams terrain chunks here). */
  update(x: number, z: number): void;
}

export function lobbySpace(world: BuiltWorld): WalkSpace {
  return { height: (x, z) => world.height(x, z), update: (x, z) => world.terrain.update(x, z) };
}

export class LocalPlayer {
  readonly camera: UniversalCamera;
  private readonly collision: CollisionWorld;
  private enabled = false;
  private vy = 0;
  private grounded = false;

  constructor(private readonly scene: Scene, private readonly canvas: HTMLCanvasElement, private readonly space: WalkSpace,
    private readonly input: InputState = sharedInput) {
    const cam = new UniversalCamera("player", new Vector3(0, 10, 0), scene);
    cam.minZ = 0.1;
    cam.maxZ = 450;
    cam.checkCollisions = false; // the walker collides the body itself (spec 2m §3.4)
    cam.applyGravity = false;
    cam.inertia = 0;
    cam.speed = 0; // movement handled manually
    cam.keysUp = []; cam.keysDown = []; cam.keysLeft = []; cam.keysRight = [];
    // Babylon's touch input walks forward on a vertical drag; the touch HUD drives the walker instead (spec 2p §3.2)
    cam.inputs.removeByType("FreeCameraTouchInput");
    this.camera = cam;
    this.collision = new CollisionWorld(scene, (x, z) => space.height(x, z));
    // look speed, inverted Y and field of view follow the saved settings and the slider preview (spec 2g §3.2)
    $cameraSettings.subscribe((s) => applyCamera(cam, cameraParams(s)));

    bindKeyboard(this.input);
    canvas.addEventListener("click", () => { if (this.enabled && !$touchMode.get() && document.pointerLockElement !== canvas) canvas.requestPointerLock(); });
    scene.onBeforeRenderObservable.add(() => this.step());
  }

  spawn(pos: { x: number; y: number; z: number }, yawDeg: number): void {
    // the server spawns on terrain; stand on the nomen stone (or any low collider) above it instead
    const top = new Vector3(pos.x, pos.y + SPAWN_LIFT, pos.z);
    const hit = this.scene.pickWithRay(new Ray(top, Vector3.Down(), SPAWN_LIFT), (m) => m.checkCollisions);
    const feetY = spawnFeetY(pos.y, hit?.pickedPoint ? hit.pickedPoint.y : null);
    this.camera.position.set(pos.x, feetY + EYE, pos.z);
    this.camera.rotation.set(0, (yawDeg * Math.PI) / 180, 0);
    this.vy = 0;
  }

  /** While a panel is open, movement keys are ignored even if no text field has focus. */
  setMovementBlocked(blocked: boolean): void {
    this.input.setBlocked(blocked);
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (on) {
      this.scene.activeCamera = this.camera;
      this.camera.attachControl(this.canvas, true);
    } else {
      this.camera.detachControl();
      this.input.clear();
    }
  }

  private step(): void {
    if (!this.enabled) return;
    const dt = Math.min(this.scene.getEngine().getDeltaTime() / 1000, 0.1);
    this.space.update(this.camera.position.x, this.camera.position.z);
    const look = this.input.takeLook();
    if (look.dx || look.dy) applyLook(this.camera.rotation, look.dx, look.dy, cameraParams($cameraSettings.get()));
    const turn = this.input.takeTurn();
    if (turn.yaw || turn.pitch) applyTurn(this.camera.rotation, turn.yaw, turn.pitch);
    const m = this.input.move();
    const speed = m.sprint ? SPRINT : WALK;
    const fwd = this.camera.getDirection(Vector3.Forward());
    fwd.y = 0; fwd.normalize();
    const right = this.camera.getDirection(Vector3.Right());
    right.y = 0; right.normalize();
    const move = fwd.scale(m.z).addInPlace(right.scale(m.x)).scaleInPlace(speed * dt);
    const p = this.camera.position;
    const next = walkStep({ x: p.x, y: p.y - EYE, z: p.z, vy: this.vy, grounded: this.grounded }, { dx: move.x, dz: move.z, jump: this.input.jump() }, dt, this.collision);
    this.vy = next.vy;
    this.grounded = next.grounded;
    p.set(next.x, next.y + EYE, next.z);
  }

  /** Feet in world space, for the trap guard (the camera sits EYE above them). */
  feetPosition(): { x: number; y: number; z: number } {
    const p = this.camera.position;
    return { x: p.x, y: p.y - EYE, z: p.z };
  }

  state(): { x: number; y: number; z: number; yaw: number; pitch: number; anim: string } {
    const p = this.camera.position;
    const m = this.input.move();
    const anim = !this.grounded ? "jump" : m.x || m.z ? (m.sprint ? "run" : "walk") : "idle";
    return { x: p.x, y: p.y - EYE, z: p.z, yaw: (this.camera.rotation.y * 180) / Math.PI, pitch: (this.camera.rotation.x * 180) / Math.PI, anim };
  }

  applyCorrection(pos: { x: number; y: number; z: number }): void {
    this.camera.position.set(pos.x, pos.y + EYE, pos.z);
    this.vy = 0;
  }
}
